/** The parts of a listing address used to place the map pin. */
export type MapAddress = {
    address?: string | null;
    city?: string | null;
    post?: string | null;
    /** The province as received; "British Columbia" or a missing value means "BC". */
    province?: string | null;
};

function clean(part: string | null | undefined): string {
    return (part ?? "").replace(/\s+/g, " ").trim();
}

/** "BC" for a missing province or "British Columbia"; any other value is used as received. */
function regionName(province: string | null | undefined): string {
    const value = clean(province);
    return !value || /^british columbia$/i.test(value) ? "BC" : value;
}

/**
 * The search text for the map: "<address>, <city>, <province> <post>, Canada", leaving out empty
 * parts. The province is "BC" unless the listing says otherwise. Returns "" when there is no
 * street address (no map is shown then).
 */
export function buildMapQuery({ address, city, post, province }: MapAddress): string {
    const street = clean(address);
    if (!street) return "";
    const region = [regionName(province), clean(post)].filter(Boolean).join(" ");
    return [street, clean(city), region, "Canada"].filter(Boolean).join(", ");
}

/** The Embed API "place" address for a query, or "" when there is no key or query. */
export function mapEmbedUrl(key: string, query: string): string {
    if (!key || !query) return "";
    return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(key)}&q=${encodeURIComponent(query)}`;
}

/** A plain link that opens the address in Google Maps; sends nothing until clicked. */
export function mapLinkUrl(query: string): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
